/**
 * Cosmetic display-name resolver — the single localization path for table
 * and tile (domino) skin names, shared by every active React consumer
 * (Settings selectors, Lobby selectors). Keyed by the STABLE cosmetic id;
 * the English catalog label is only the fallback for an unknown future id.
 * Ids, assets, texture keys and API payloads are never touched, and no
 * English label is ever parsed to determine identity.
 *
 * Resolution happens at call time against the shared i18next instance, so a
 * component that re-renders on a language switch (via useTranslation) always
 * shows the active locale's name.
 */
import { t } from './index';
import enCosmetics from './locales/en/cosmetics.json';

type TableSkinKey = keyof typeof enCosmetics.table;
type TileSkinKey = keyof typeof enCosmetics.tile;

const TABLE_KEYS: ReadonlySet<string> = new Set(Object.keys(enCosmetics.table));
const TILE_KEYS: ReadonlySet<string> = new Set(Object.keys(enCosmetics.tile));

function isTableKey(id: string): id is TableSkinKey {
  return TABLE_KEYS.has(id);
}
function isTileKey(id: string): id is TileSkinKey {
  return TILE_KEYS.has(id);
}

/** Localized table-skin display name; unknown id → its catalog label verbatim. */
export function tableSkinDisplayName(id: string, fallbackLabel: string): string {
  return isTableKey(id) ? t(`cosmetics:table.${id}`) : fallbackLabel;
}

/** Localized tile-skin display name; unknown id → its catalog label verbatim. */
export function tileSkinDisplayName(id: string, fallbackLabel: string): string {
  return isTileKey(id) ? t(`cosmetics:tile.${id}`) : fallbackLabel;
}
